/** `FAILED`: o provisionamento falhou; `failureReason` diz porquê e o retry volta a tentar. */
export const TENANT_STATUSES = ["PROVISIONING", "ACTIVE", "SUSPENDED", "CANCELLED", "FAILED"] as const;

export type TenantStatus = (typeof TENANT_STATUSES)[number];

/** Núcleo do sistema: toda empresa tem, não entra no contrato de módulos. */
export const CORE_MODULES = ["projects", "records", "settings"] as const;
type CoreModule = (typeof CORE_MODULES)[number];

/** Módulos contratáveis, na ordem canônica em que são enviados ao backend. */
export const TENANT_MODULES = [
    "dashboards",
    "financial",
    "inventory",
    "tickets",
    "crm",
    "contact",
    "website",
    "checkin",
    "appHub",
] as const;

export type TenantModule = (typeof TENANT_MODULES)[number];

export type TenantLogoVariant = "mark" | "wordmark";

const DOMAIN_STATUSES = ["NONE", "PENDING", "VERIFIED", "FAILED"] as const;
export type DomainStatus = (typeof DOMAIN_STATUSES)[number];

export interface TenantSite {
    subdomain: string;
    customDomain: string | null;
    domainStatus: DomainStatus;
    /** Destino do CNAME que o tenant cria no DNS dele. */
    cnameTarget: string;
    published: boolean;
}

export type TenantSiteUpdate = Pick<TenantSite, "subdomain" | "customDomain" | "published">;

export const REPORT_FONTS = ["Arial", "Calibri", "Georgia", "Verdana"] as const;
type ReportFont = (typeof REPORT_FONTS)[number];

export const REPORT_LOGO_POSITIONS = ["left", "center", "right", "none"] as const;
export type ReportLogoPosition = (typeof REPORT_LOGO_POSITIONS)[number];

export interface TenantReportDesign {
    cover: boolean;
    backCover: boolean;
    headerLogo: ReportLogoPosition;
    headerText: string | null;
    font: ReportFont;
}

export const TENANT_APPS = ["worker", "gestao"] as const;
export type TenantApp = (typeof TENANT_APPS)[number];

interface TenantApps {
    enabled: TenantApp[];
    /** Código que o funcionário digita (ou lê no QR) no primeiro acesso. */
    companyCode: string;
}

const ADMIN_INVITE_STATUSES = ["PENDING", "ACCEPTED", "EXPIRED"] as const;
type AdminInviteStatus = (typeof ADMIN_INVITE_STATUSES)[number];

/** Convite do primeiro administrador, enviado quando a empresa é criada. */
interface TenantAdminInvite {
    name: string;
    email: string;
    status: AdminInviteStatus;
    sentAt: string;
    expiresAt: string;
}

export interface PlatformTenantSummary {
    id: string;
    name: string;
    slug: string;
    status: TenantStatus;
    userCount: number;
    activeProjectCount: number;
    lastAccessAt: string | null;
    createdAt: string;
}

export interface PlatformTenantDetail extends PlatformTenantSummary {
    locale: string;
    currency: string;
    logoUrl: string | null;
    logoWordmarkUrl: string | null;
    websiteUrl: string | null;
    brandColor: string | null;
    reportFooter: string | null;
    modules: string[];
    site: TenantSite;
    reportDesign: TenantReportDesign;
    apps: TenantApps;
    contactName: string | null;
    contactEmail: string | null;
    suspendedReason: string | null;
    suspendedAt: string | null;
    cancelledAt: string | null;
    adminInvite: TenantAdminInvite | null;
    schemaName: string;
    adminEmail: string | null;
    failureReason: string | null;
}

export interface PlatformTenantCreate {
    name: string;
    slug: string;
    locale: string;
    currency: string;
    modules: TenantModule[];
    apps: TenantApp[];
    admin: { name: string; email: string };
}

export type TenantLifecycleAction = "suspend" | "reactivate" | "cancel";

export interface PlatformTenantBrandingUpdate {
    name: string;
    brandColor: string | null;
    locale: string;
    currency: string;
    websiteUrl: string | null;
    reportFooter: string | null;
}

export interface PlatformTenantPage {
    content: PlatformTenantSummary[];
    totalElements: number;
    number: number;
    size: number;
}

export interface PlatformTenantListParams {
    page: number;
    size: number;
    q?: string;
    status?: TenantStatus;
}

/** Totais da visão geral do console (GET /platform/tenants/stats). */
export interface PlatformStats {
    byStatus: Record<TenantStatus, number>;
    totalTenants: number;
    /** Vínculos ACTIVE, sem as organizações canceladas. */
    activeUsers: number;
    activeProjects: number;
    createdLast30Days: number;
}

export const MEMBERSHIP_STATUSES = ["INVITED", "ACTIVE", "SUSPENDED", "REMOVED"] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

/** Pessoa de uma organização na aba Membros (só leitura). */
export interface TenantMember {
    identityId: string;
    email: string;
    name: string | null;
    status: MembershipStatus;
    lastLoginAt: string | null;
    joinedAt: string;
}

export interface TenantMemberPage {
    content: TenantMember[];
    totalElements: number;
    number: number;
    size: number;
}

export interface TenantMemberListParams {
    page: number;
    size: number;
    q?: string;
    status?: MembershipStatus;
}

export const TENANT_AUDIT_ACTIONS = [
    "CREATED",
    "PROVISIONING_RETRIED",
    "SUSPENDED",
    "REACTIVATED",
    "CANCELLED",
    "BRANDING_UPDATED",
    "MODULES_UPDATED",
    "APPS_UPDATED",
    "COMPANY_CODE_REGENERATED",
    "REPORT_DESIGN_UPDATED",
    "SITE_UPDATED",
    "SITE_VERIFIED",
    "ADMIN_INVITE_RESENT",
    "TOCONLINE_UPDATED",
    "TOCONLINE_DISCONNECTED",
    "IMPERSONATED",
] as const;
export type TenantAuditAction = (typeof TENANT_AUDIT_ACTIONS)[number];

/** Evento da aba Auditoria; `target*` só nas sessões de suporte. */
export interface TenantAuditEntry {
    id: string;
    action: TenantAuditAction;
    detail: string | null;
    createdAt: string;
    actorId: string | null;
    actorEmail: string | null;
    targetId: string | null;
    targetEmail: string | null;
}

export interface TenantAuditPage {
    content: TenantAuditEntry[];
    totalElements: number;
    number: number;
    size: number;
}
